import type { TicketStatus } from '@nadoutong/contracts';
import { TICKET_TEXT_MAX_LENGTH } from '@nadoutong/contracts';
import { MessageSquare, Send } from 'lucide-react';
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { TICKETS_QUERY_KEY, useCreateTicketReply, useTicketReplies } from '../../api/useTickets.ts';
import { formatTimestamp } from '../../components/formatters.ts';
import { useToast } from '../../components/Toast.tsx';
import { useT } from '../../i18n/index.ts';
import { Button, Skeleton, Textarea, cx } from '../../ui/index.ts';
import { TicketAttachments } from './TicketAttachments.tsx';
import { describeReplyError, describeTicketLoadError } from './ticketMeta.ts';

/**
 * One ticket's reply thread + composer (tenant #116).
 *
 * Replies arrive in `createdAt` ASCENDING order and are rendered as
 * received — never re-sorted (contract). Sending a reply invalidates
 * the whole `['tickets']` prefix (see `useCreateTicketReply`): the
 * server auto-reopens `resolved → processing` in the same transaction,
 * so the LIST must refresh with the thread or its status chip lies.
 *
 * A closed ticket disables the composer up front; the 409 TICKET_CLOSED
 * branch still exists for the race where the local row is stale, and it
 * refreshes the list so the composer disables itself.
 */
interface Props {
  readonly ticketId: string;
  readonly status: TicketStatus;
  readonly attachmentCount: number;
}

export function TicketReplyThread({ ticketId, status, attachmentCount }: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [content, setContent] = useState('');
  const closed = status === 'closed';

  const repliesQ = useTicketReplies(ticketId);
  const sendReply = useCreateTicketReply();
  const trimmed = content.trim();

  function submit(): void {
    if (trimmed.length === 0 || trimmed.length > TICKET_TEXT_MAX_LENGTH) {
      return;
    }
    sendReply.mutate(
      { ticketId, content: trimmed },
      {
        onSuccess: () => {
          setContent('');
        },
        onError: (error) => {
          const presentation = describeReplyError(error, t);
          toast.error(presentation.message);
          if (presentation.refreshTickets) {
            void qc.invalidateQueries({ queryKey: TICKETS_QUERY_KEY });
          }
        },
      },
    );
  }

  const replies = repliesQ.data?.replies ?? [];

  return (
    <div
      className="flex flex-col gap-3 border-t border-line-soft bg-raised px-4 py-3"
      data-testid={`tickets-thread-${ticketId}`}
    >
      {attachmentCount > 0 ? <TicketAttachments ticketId={ticketId} /> : null}
      {repliesQ.isLoading ? (
        <div className="flex flex-col gap-2" data-testid={`tickets-replies-loading-${ticketId}`}>
          <Skeleton className="h-8" />
          <span className="sr-only">{t('tickets.replies.loading')}</span>
        </div>
      ) : repliesQ.isError ? (
        <p
          role="alert"
          className="m-0 text-caption text-bad"
          data-testid={`tickets-replies-error-${ticketId}`}
        >
          {t('tickets.replies.error', { message: describeTicketLoadError(repliesQ.error, t) })}
        </p>
      ) : replies.length === 0 ? (
        <p
          className="m-0 flex items-center gap-1.5 text-caption text-ink-low"
          data-testid={`tickets-replies-empty-${ticketId}`}
        >
          <MessageSquare aria-hidden="true" className="h-3.5 w-3.5" />
          {t('tickets.replies.empty')}
        </p>
      ) : (
        <ul className="m-0 flex list-none flex-col gap-2.5 p-0">
          {replies.map((reply) => {
            const isVendor = reply.author === 'vendor';
            return (
              <li key={reply.id} className="flex gap-2" data-testid={`tickets-reply-${reply.id}`}>
                <MessageSquare
                  aria-hidden="true"
                  className={cx(
                    'mt-0.5 h-3.5 w-3.5 shrink-0',
                    isVendor ? 'text-forest' : 'text-ink-low',
                  )}
                />
                <div className="min-w-0 flex-1">
                  <p className="m-0 flex flex-wrap items-baseline gap-x-2 text-caption text-ink-low">
                    <span className={cx('font-medium', isVendor ? 'text-forest' : 'text-ink-mid')}>
                      {isVendor
                        ? t('tickets.replies.author.vendor')
                        : t('tickets.replies.author.user')}
                    </span>
                    <span className="font-mono tabular-nums">
                      {formatTimestamp(reply.createdAt)}
                    </span>
                  </p>
                  <p className="m-0 mt-0.5 whitespace-pre-wrap break-words text-body text-ink">
                    {reply.content}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}

      <div className="flex items-start gap-2">
        <Textarea
          value={content}
          rows={2}
          maxLength={TICKET_TEXT_MAX_LENGTH}
          disabled={closed || sendReply.isPending}
          placeholder={
            closed ? t('tickets.replies.closed_placeholder') : t('tickets.replies.placeholder')
          }
          data-testid={`tickets-reply-input-${ticketId}`}
          onChange={(event) => setContent(event.currentTarget.value)}
        />
        <Button
          variant="primary"
          size="sm"
          className="mt-0.5 shrink-0"
          disabled={closed || trimmed.length === 0}
          loading={sendReply.isPending}
          data-testid={`tickets-reply-send-${ticketId}`}
          onClick={submit}
        >
          <Send aria-hidden="true" className="h-3.5 w-3.5" />
          {t('tickets.replies.send')}
        </Button>
      </div>
      <p className="m-0 flex items-baseline justify-between gap-2 text-caption text-ink-low">
        <span>{t('tickets.replies.footer')}</span>
        <span className="font-mono text-mono-label tabular-nums">
          {content.length}/{TICKET_TEXT_MAX_LENGTH}
        </span>
      </p>
    </div>
  );
}
